import moment from "moment";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { getPromotions, deletePromotion } from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../helpers/FilterSearchHelper";
import { getOrganizationId } from "../../api/Helper";
import PromotionsMockup from "./PromotionsMockup";

import {
  Table,
  Button,
  message,
  Modal,
  Spin,
  Menu,
  Dropdown,
  Input,
  Select,
  Tag,
  Tooltip,
  Grid,
  List,
  Card,
  Pagination,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  PlusOutlined,
  MoreOutlined,
  SearchOutlined,
  ClearOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import { RxStar } from "react-icons/rx";
import PageHeader from "../../components/pageheader/PageHeader";
import EmptyState from "../../components/emptystate/EmptyState";
import { getTablePagination } from "../../helpers/TablePaginationHelper";
import { COMMERCIAL_INTELLIGENCE_PATHS } from "../../helpers/MenuPathHelper";

import styles from "./Promotions.module.scss";

const Promotions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(0);
  const [isOrg5, setIsOrg5] = useState(false);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();
  const [nameFilterValue, setNameFilterValue] = useState("");
  const debouncedNameFilterValue = useDebouncedValue(nameFilterValue);
  const [externalIdFilterValue, setExternalIdFilterValue] = useState("");
  const debouncedExternalIdFilterValue = useDebouncedValue(externalIdFilterValue);
  const [statusFilterValue, setStatusFilterValue] = useState<
    string | undefined
  >(undefined);
  const [typeFilterValue, setTypeFilterValue] = useState<string | undefined>(
    undefined,
  );
  const [originFilterValue, setOriginFilterValue] = useState<
    string | undefined
  >("SUGGESTED");
  const [brandIdFilterValue, setBrandIdFilterValue] = useState<number | undefined>(
    searchParams.get("brandId") ? Number(searchParams.get("brandId")) : undefined
  );

  useEffect(() => {
    const fetchOrg = async () => {
      const orgId = await getOrganizationId();
      setIsOrg5(parseInt(orgId) === 5);
    };
    fetchOrg();
  }, []);

  const handleFetchPromotions = useCallback(
    async (pagination = actualPage) => {
      const requestId = beginRequest();
      setLoading(true);

      let statusEnum = undefined;
      if (statusFilterValue) {
        statusEnum = statusFilterValue;
      }

      const response = await getPromotions(pagination, {
        name: debouncedNameFilterValue,
        externalId: debouncedExternalIdFilterValue,
        status: statusEnum,
        type: typeFilterValue,
        origin: originFilterValue,
        brandId: brandIdFilterValue,
      });

      if (isStaleRequest(requestId)) return;

      const allPromotions = response.promotions;
      const promotionsWithKey: any[] = [];

      allPromotions.forEach((promotion: any) => {
        promotion.key = promotion.id;
        promotionsWithKey.push(promotion);
      });

      setData(promotionsWithKey);
      setTotalSize(response.totalElements);
      setLoading(false);
    },
    [
      actualPage,
      debouncedNameFilterValue,
      debouncedExternalIdFilterValue,
      statusFilterValue,
      typeFilterValue,
      originFilterValue,
      brandIdFilterValue,
      beginRequest,
      isStaleRequest,
    ],
  );

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_PROMOTIONS)) {
      handleFetchPromotions();
    }
  }, [handleFetchPromotions]);

  const showDeleteConfirm = (itemId: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDelete(itemId);
      },
    });
  };

  const handleDelete = async (promotionId: number) => {
    setLoading(true);
    const deleteStatus = await deletePromotion(promotionId);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await handleFetchPromotions();
    setLoading(false);
  };

  const handlePagination = (page: number) => {
    const zeroBasedPage = page - 1;
    setActualPage(zeroBasedPage);
    handleFetchPromotions(zeroBasedPage);
  };

  const handleClearFilters = () => {
    setNameFilterValue("");
    setExternalIdFilterValue("");
    setStatusFilterValue(undefined);
    setTypeFilterValue(undefined);
    setOriginFilterValue("SUGGESTED");
    setBrandIdFilterValue(undefined);
    setActualPage(0);
  };

  const hasActiveFilters = () => {
    return (
      debouncedNameFilterValue !== "" ||
      debouncedExternalIdFilterValue !== "" ||
      statusFilterValue !== undefined ||
      typeFilterValue !== undefined ||
      originFilterValue !== "SUGGESTED" ||
      brandIdFilterValue !== undefined
    );
  };

  const hasNoData = () => {
    return !loading && data.length === 0;
  };

  const hasNoDataDueToFilters = () => {
    return hasNoData() && hasActiveFilters();
  };

  const hasNoDataAtAll = () => {
    return hasNoData() && !hasActiveFilters();
  };

  const getEmptyState = () => {
    if (hasNoDataDueToFilters()) {
      return <EmptyState type="noResults" entityName="Promotion" />;
    }

    if (hasNoDataAtAll()) {
      return (
        <EmptyState
          type="noData"
          entityName="Promotion"
        />
      );
    }

    return null;
  };

  const isSuggestedRecommendation = (record: any) => {
    const originValue =
      typeof record.origin === "string" ? record.origin : record.origin?.value;
    const statusValue =
      typeof record.status === "string" ? record.status : record.status?.value;
    return (
      originValue === "SUGGESTED" &&
      (statusValue === "CREATED" || statusValue === "SUGGESTED")
    );
  };

  const formatMetric = (value?: number | null) => {
    if (value === null || value === undefined) return "";
    return value.toLocaleString("es-UY", { maximumFractionDigits: 2 });
  };

  const renderOriginTag = (origin: any) => {
    if (!origin) return null;

    const getOriginColor = (originValue: string) => {
      switch (originValue) {
        case "MANUAL":
          return "blue";
        case "SUGGESTED":
          return "purple";
        default:
          return "default";
      }
    };

    const originValue = typeof origin === "string" ? origin : origin.value;
    const originDescription =
      typeof origin === "string"
        ? origin === "MANUAL"
          ? "Manual"
          : origin === "SUGGESTED"
            ? "Sugerida"
            : origin
        : origin.description_es;

    return (
      <Tag color={getOriginColor(originValue)}>{originDescription}</Tag>
    );
  };

  const renderStatusTag = (status: any) => (
    <Tag
      color={
        status?.value === "ACTIVE"
          ? "green"
          : status?.value === "CREATED"
            ? "blue"
            : status?.value === "SUGGESTED"
              ? "purple"
              : "red"
      }
    >
      {status?.description_es}
    </Tag>
  );

  const renderPromotionActions = (record: any) => (
    <Dropdown
      overlay={
        <Menu>
          {hasPermission(privilegesEnum.WRITE_PROMOTIONS) ? (
            <Menu.Item
              key="edit"
              onClick={() =>
                navigate(`${COMMERCIAL_INTELLIGENCE_PATHS.promotions}/${record.id}/view`)
              }
              icon={<EditOutlined />}
            >
              {t("Common-Edit")}
            </Menu.Item>
          ) : (
            <Menu.Item
              key="view"
              onClick={() =>
                navigate(`${COMMERCIAL_INTELLIGENCE_PATHS.promotions}/${record.id}/view`)
              }
              icon={<EyeOutlined />}
            >
              {t("Common-View")}
            </Menu.Item>
          )}

          {hasPermission(privilegesEnum.DELETE_PROMOTIONS) && (
            <Menu.Item
              key="delete"
              onClick={() => {
                showDeleteConfirm(record.key);
              }}
              icon={<DeleteOutlined />}
            >
              {t("Common-Delete")}
            </Menu.Item>
          )}

          <Menu.Item
            key="createCampaign"
            icon={<NotificationOutlined />}
            onClick={() =>
              navigate(`${COMMERCIAL_INTELLIGENCE_PATHS.campaigns}/create`, {
                state: { promotionId: record.id, promotionName: record.name },
              })
            }
          >
            {t("Common-NewCampaign")}
          </Menu.Item>
        </Menu>
      }
    >
      <Button className={styles.actionButtonMore}>
        <MoreOutlined className={styles.actionIconMore} />
      </Button>
    </Dropdown>
  );

  const screens = Grid.useBreakpoint();
  const isMobileList = !screens.md;

  const paginationConfig = getTablePagination(t, {
    current: actualPage + 1,
    total: totalSize,
    onChange: handlePagination,
  });

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: t("Common-Name"),
        dataIndex: "name",
        key: "name",
        width: 260,
        className: styles.wrapCell,
        render: (name: string, record: any) => (
          <a
            href={`${COMMERCIAL_INTELLIGENCE_PATHS.promotions}/${record.id}/view`}
            className={styles.nameLink}
          >
            {name}
          </a>
        ),
      },
    ];

    if (screens.lg) {
      cols.push({
        title: t("Common-ExternalId"),
        dataIndex: "externalId",
        key: "externalId",
        width: 110,
        ellipsis: true,
      });
    }

    if (screens.xl) {
      cols.push({
        title: t("Common-Description"),
        dataIndex: "description",
        key: "description",
        width: 280,
        className: styles.wrapCell,
      });
    }

    if (screens.lg) {
      cols.push({
        title: t("Common-Type"),
        dataIndex: "type",
        key: "type",
        width: 100,
        ellipsis: true,
        render: (type: any) => type?.description_es,
      });
    }

    cols.push({
      title: t("Common-Origin"),
      dataIndex: "origin",
      key: "origin",
      width: 100,
      className: styles.tagCell,
      render: (origin: any) => (
        <span className={styles.cellTag}>{renderOriginTag(origin)}</span>
      ),
    });

    if (screens.xl) {
      cols.push(
        {
          title: t("Promotions-RevenueLift"),
          dataIndex: "estimatedRevenueLift",
          key: "estimatedRevenueLift",
          width: 110,
          align: "right" as const,
          render: (value: number, record: any) =>
            isSuggestedRecommendation(record) ? formatMetric(value) : "",
        },
        {
          title: t("Promotions-NetRoi"),
          dataIndex: "estimatedNetRoi",
          key: "estimatedNetRoi",
          width: 90,
          align: "right" as const,
          render: (value: number, record: any) =>
            isSuggestedRecommendation(record) ? formatMetric(value) : "",
        },
        {
          title: t("Promotions-Rank"),
          dataIndex: "recommendationRank",
          key: "recommendationRank",
          width: 70,
          align: "center" as const,
          render: (value: number, record: any) =>
            isSuggestedRecommendation(record) && value ? value : "",
        },
      );
    }

    cols.push({
      title: t("Common-Status"),
      dataIndex: "status",
      key: "status",
      width: 110,
      className: styles.tagCell,
      render: (status: any) => (
        <span className={styles.cellTag}>{renderStatusTag(status)}</span>
      ),
    });

    if (screens.lg) {
      cols.push({
        title: t("Common-StartDate"),
        dataIndex: "startDate",
        key: "startDate",
        width: 155,
        ellipsis: true,
        render: (date: any) =>
          date
            ? moment(date, "YYYY-MM-DDHH:mm:ss").format("DD-MM-YYYY HH:mm:ss")
            : "",
      });
    }

    if (screens.xl) {
      cols.push({
        title: t("Common-EndDate"),
        dataIndex: "endDate",
        key: "endDate",
        width: 155,
        ellipsis: true,
        render: (date: any) =>
          date
            ? moment(date, "YYYY-MM-DDHH:mm:ss").format("DD-MM-YYYY HH:mm:ss")
            : "",
      });
    }

    cols.push({
      title: t("Common-Actions"),
      key: "action",
      width: 88,
      className: styles.actionsCell,
      render: (_text: any, record: any) => renderPromotionActions(record),
    });

    return cols;
  }, [t, screens.lg, screens.xl, location.pathname, navigate]);

  const tableScrollX = useMemo(
    () => columns.reduce((sum, col) => sum + (col.width ?? 120), 0),
    [columns],
  );

  if (isOrg5) {
    return <PromotionsMockup />;
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("Common-Promotions")}
        icon={<RxStar />}
        showCreateButton={false}
      />
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <div className={styles.headerTableWrapper}>
            <div className={styles.filtersWrapper}>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-SearchByName")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setNameFilterValue(e.target.value);
                    setActualPage(0);
                  }}
                  value={nameFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-SearchByExternalId")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setExternalIdFilterValue(e.target.value);
                    setActualPage(0);
                  }}
                  value={externalIdFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  showSearch
                  allowClear
                  value={statusFilterValue}
                  className={styles.filterSelect}
                  placeholder={t("Common-Status")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(_, option) => {
                    if (option) {
                      setStatusFilterValue(option.value);
                      setActualPage(0);
                    }
                  }}
                  onClear={() => {
                    setStatusFilterValue(undefined);
                    setActualPage(0);
                  }}
                >
                  <Select.Option value="CREATED">
                    {t("Common-Created")}
                  </Select.Option>
                  <Select.Option value="SCHEDULED">
                    {t("Common-Scheduled")}
                  </Select.Option>
                  <Select.Option value="IN_ACTION">
                    {t("Common-InAction")}
                  </Select.Option>
                  <Select.Option value="CANCEL">
                    {t("Common-Cancelled")}
                  </Select.Option>
                  <Select.Option value="FINISHED">
                    {t("Common-Finished")}
                  </Select.Option>
                </Select>
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  showSearch
                  allowClear
                  value={typeFilterValue}
                  className={styles.filterSelect}
                  placeholder={t("Common-Type")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(_, option) => {
                    if (option) {
                      setTypeFilterValue(option.value);
                      setActualPage(0);
                    }
                  }}
                  onClear={() => {
                    setTypeFilterValue(undefined);
                    setActualPage(0);
                  }}
                >
                  <Select.Option value="REGULAR">
                    {t("Common-Regular")}
                  </Select.Option>
                  <Select.Option value="BOGO">{t("Common-BOGO")}</Select.Option>
                  <Select.Option value="MxN">{t("Common-MxN")}</Select.Option>
                  <Select.Option value="BUY_TOGETHER">
                    {t("Common-BuyTogether")}
                  </Select.Option>
                  <Select.Option value="PROGRESSIVE_DISCOUNT">
                    {t("Common-ProgressiveDiscount")}
                  </Select.Option>
                </Select>
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  showSearch
                  allowClear
                  value={originFilterValue}
                  className={styles.filterSelect}
                  placeholder={t("Common-Origin")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(_, option) => {
                    if (option) {
                      setOriginFilterValue(option.value);
                      setActualPage(0);
                    }
                  }}
                  onClear={() => {
                    setOriginFilterValue(undefined);
                    setActualPage(0);
                  }}
                >
                  <Select.Option value="MANUAL">Manual</Select.Option>
                  <Select.Option value="SUGGESTED">Sugerida</Select.Option>
                </Select>
              </div>
              <div className={styles.filterClearActions}>
                <Tooltip title={t("Common-Clear")}>
                  <Button
                    onClick={handleClearFilters}
                    className={styles.clearButton}
                    icon={<ClearOutlined />}
                    disabled={!hasActiveFilters()}
                  >
                    {t("Common-Clear")}
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>

          {isMobileList ? (
            <div className={styles.cardList}>
              {loading ? (
                <div className={styles.cardListLoading}>
                  <Spin />
                </div>
              ) : data.length === 0 ? (
                getEmptyState()
              ) : (
                <>
                  <List
                    dataSource={data}
                    renderItem={(record: any) => (
                      <List.Item className={styles.cardListItem}>
                        <Card className={styles.promotionCard} bordered={false}>
                          <div className={styles.cardHeader}>
                            <a
                              href={`${COMMERCIAL_INTELLIGENCE_PATHS.promotions}/${record.id}/view`}
                              className={styles.nameLink}
                            >
                              {record.name}
                            </a>
                            {renderPromotionActions(record)}
                          </div>
                          <div className={styles.cardTags}>
                            {renderOriginTag(record.origin)}
                            {renderStatusTag(record.status)}
                            {record.type?.description_es && (
                              <Tag>{record.type.description_es}</Tag>
                            )}
                          </div>
                          {isSuggestedRecommendation(record) && (
                            <div className={styles.cardMetrics}>
                              <Typography.Text type="secondary">
                                {t("Promotions-RevenueLift")}:{" "}
                                {formatMetric(record.estimatedRevenueLift) || "—"}
                              </Typography.Text>
                              <Typography.Text type="secondary">
                                {t("Promotions-NetRoi")}:{" "}
                                {formatMetric(record.estimatedNetRoi) || "—"}
                              </Typography.Text>
                              {record.recommendationRank ? (
                                <Typography.Text type="secondary">
                                  {t("Promotions-Rank")}: {record.recommendationRank}
                                </Typography.Text>
                              ) : null}
                            </div>
                          )}
                        </Card>
                      </List.Item>
                    )}
                  />
                  <div className={styles.cardPagination}>
                    <Pagination {...paginationConfig} />
                  </div>
                </>
              )}
            </div>
          ) : (
            <Table
              size="small"
              className={styles.table}
              loading={loading}
              columns={columns}
              dataSource={data}
              rowKey={(record) => record.id}
              tableLayout="fixed"
              scroll={{ x: tableScrollX }}
              pagination={paginationConfig}
              locale={{
                emptyText: getEmptyState(),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Promotions;
